import { useRef } from "react";
import { PiggyBank } from "lucide-react";
import {
  useCompositionPricing,
  useCompositionsList,
  useCurrentComposition
} from "@design-lab/entities/composition";
import { formatCurrency } from "common/utils/format/number";
import { pluralize } from "common/utils/format/word";
import { usePriceExposedTracking } from "@design-lab/shared/hooks/usePriceExposedTracking";
import { deriveSizeTier } from "@design-lab/shared/lib/analytics";

const getProgressColor = (percentage: number) => {
  if (percentage < 10) return { bg: "bg-red-400", icon: "text-red-400" };
  if (percentage < 20) return { bg: "bg-orange-500", icon: "text-orange-500" };
  if (percentage < 30) return { bg: "bg-amber-400", icon: "text-amber-400" };
  if (percentage < 40) return { bg: "bg-yellow-400", icon: "text-yellow-400" };
  if (percentage < 50) return { bg: "bg-lime-400", icon: "text-lime-400" };
  if (percentage < 60) return { bg: "bg-green-400", icon: "text-green-400" };
  if (percentage < 70) return { bg: "bg-emerald-500", icon: "text-emerald-500" };
  if (percentage < 80) return { bg: "bg-teal-400", icon: "text-teal-400" };
  if (percentage < 90) return { bg: "bg-blue-500", icon: "text-blue-500" };
  if (percentage < 100) return { bg: "bg-indigo-500", icon: "text-indigo-500" };
  return { bg: "bg-violet-500", icon: "text-violet-500" };
};

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7.5" cy="7.5" r="7.5" fill="white" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.5 0C3.35786 0 0 3.35786 0 7.5C0 11.6421 3.35786 15 7.5 15C11.6421 15 15 11.6421 15 7.5C15 3.35786 11.6421 0 7.5 0ZM11.0503 5.93666C11.3166 5.6704 11.3166 5.23869 11.0503 4.97243C10.784 4.70616 10.3523 4.70616 10.0861 4.97243L6.47727 8.58122L4.91394 7.01788C4.64767 6.75162 4.21597 6.75162 3.9497 7.01788C3.68343 7.28415 3.68343 7.71585 3.9497 7.98212L5.99515 10.0276C6.26142 10.2938 6.69312 10.2938 6.95939 10.0276L11.0503 5.93666Z"
      fill="currentColor"
    />
  </svg>
);

const DollarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7.5" cy="7.5" r="7.5" fill="white" />
    <path
      d="M7.5 15C11.6411 15 15 11.6425 15 7.5031C15 3.36367 11.6411 0 7.5 0C3.35886 0 0 3.35747 0 7.4969C0 11.6363 3.35886 15 7.5 15ZM4.87997 10.4634C4.94826 10.2151 5.01656 9.97311 5.09106 9.72486C5.17798 9.43939 5.25869 9.40836 5.51945 9.54489C5.96647 9.77451 6.43833 9.90484 6.93502 9.9669C7.25166 10.0041 7.56829 9.97311 7.8601 9.84278C8.41267 9.60075 8.49959 8.96152 8.03394 8.57675C7.87252 8.44642 7.69247 8.34712 7.50621 8.26645C7.02194 8.05544 6.51904 7.89408 6.0596 7.62102C5.32078 7.18039 4.84892 6.56599 4.9048 5.66612C4.96689 4.64832 5.54429 4.0091 6.47558 3.67398C6.86051 3.53124 6.86051 3.53744 6.86672 3.13405C6.86672 2.99752 6.86672 2.86098 6.86672 2.72445C6.87293 2.42036 6.92881 2.37071 7.22682 2.3583C7.31995 2.3583 7.41308 2.3583 7.50621 2.3583C8.1519 2.3583 8.1519 2.3583 8.1519 3.00372C8.1519 3.45676 8.1519 3.46297 8.60513 3.53124C8.95282 3.58709 9.28187 3.68639 9.60472 3.82913C9.78477 3.90981 9.84685 4.03393 9.79718 4.22011C9.71647 4.49938 9.64197 4.77865 9.54884 5.05172C9.46192 5.31237 9.38121 5.34961 9.13286 5.23169C8.62997 4.98966 8.10223 4.88415 7.54346 4.91518C7.39445 4.92139 7.25786 4.94001 7.12127 5.00207C6.64321 5.21307 6.5625 5.74059 6.97227 6.06951C7.17715 6.23707 7.41308 6.35499 7.66142 6.45428C8.08361 6.62805 8.512 6.80182 8.92177 7.02524C10.207 7.73893 10.5546 9.3525 9.64818 10.451C9.31912 10.8482 8.89694 11.115 8.39404 11.2516C8.17674 11.3136 8.0774 11.4253 8.08982 11.6487C8.10223 11.8722 8.08982 12.0894 8.08982 12.3128C8.08982 12.5114 7.99048 12.6169 7.7918 12.6231C7.55588 12.6293 7.31374 12.6293 7.07781 12.6231C6.86672 12.6169 6.76738 12.499 6.76738 12.2942C6.76738 12.1328 6.76738 11.9715 6.76118 11.8101C6.75497 11.4564 6.74876 11.4377 6.40108 11.3819C5.96026 11.3136 5.53187 11.2143 5.12831 11.0157C4.82409 10.873 4.79305 10.7923 4.87997 10.4634Z"
      fill="#E5E7EB"
    />
  </svg>
);

export function BulkSavings() {
  const {
    savings,
    shirtsForNextTier,
    savingsForNextTier,
    totalTiers,
    tiersMet,
    totalPriceWithBulkDiscount
  } = useCompositionPricing();
  const progressPercentage = totalTiers > 0 ? (tiersMet / totalTiers) * 100 : 0;
  const color = getProgressColor(progressPercentage);
  const { currentComposition, currentProduct } = useCurrentComposition();
  const { formCompositions } = useCompositionsList();
  const variants = formCompositions.find(c => c.id === currentComposition?.id)?.variants ?? {};
  const totalQuantity = formCompositions.reduce(
    (acc, comp) => acc + Object.values(comp.variants ?? {}).reduce((sum, qty) => sum + qty, 0),
    0
  );

  const selectedSizes =
    currentComposition?.variants?.filter(v => (variants[v.id] ?? 0) > 0).map(v => v.size) ?? [];
  const sizeTier = deriveSizeTier(selectedSizes);
  const unitPrice = totalQuantity > 0 ? totalPriceWithBulkDiscount / totalQuantity : undefined;

  const bulkSavingsRef = useRef<HTMLDivElement>(null);

  usePriceExposedTracking(
    bulkSavingsRef,
    {
      component: "BulkSavings",
      step: "DesignLab",
      priceShown: savings,
      catalogNumber: currentProduct?.catalogNumber,
      quantity: totalQuantity,
      businessLine: "print",
      ...(unitPrice !== undefined && { unitPrice }),
      totalPrice: totalPriceWithBulkDiscount,
      ...(sizeTier && { sizeTier })
    },
    totalQuantity > 0
  );

  return (
    <div
      ref={bulkSavingsRef}
      className="flex flex-col gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2"
    >
      <div className="flex items-center justify-center gap-2 lg:justify-start">
        <PiggyBank />

        <span className="text-base font-semibold uppercase text-gray-900">Buy more, save more</span>
      </div>

      <div className="relative flex items-center gap-2">
        <div className="h-2 w-full rounded-[20px] bg-gray-200">
          <div
            data-testid="buy-more-save-more-progress-bar"
            className={`h-full rounded-[20px] transition-all duration-150 ${color.bg}`}
            style={{
              width: `${progressPercentage}%`
            }}
          />
        </div>

        {Boolean(progressPercentage) && (
          <div
            className={`absolute top-0 ml-[-5px] mt-[-4px] flex h-[15px] w-[15px] items-center justify-center rounded-full transition-all duration-150 ${color.icon}`}
            style={{ left: `${progressPercentage}%` }}
          >
            <CheckIcon />
          </div>
        )}

        {totalTiers > tiersMet &&
          Array.from({ length: totalTiers - tiersMet }, (_, index) => {
            const tierIndex = tiersMet + index + 1;
            const tierPercentage = (tierIndex / totalTiers) * 100;

            return (
              <div
                key={tierIndex}
                className="absolute top-0 ml-[-5px] mt-[-4px] flex h-[15px] w-[15px] items-center justify-center rounded-full bg-gray-300 transition-all duration-150"
                style={{ left: `${tierPercentage}%` }}
              >
                <DollarIcon />
              </div>
            );
          })}
      </div>

      {totalQuantity ? (
        <div className="flex min-h-5 flex-row flex-wrap items-center justify-center gap-2 lg:justify-between">
          <div className="hidden items-center gap-1 text-xs font-medium text-gray-700 lg:order-last lg:inline-flex">
            Total savings:
            <span className="font-bold text-green-700">{formatCurrency(savings)}</span>
          </div>

          {shirtsForNextTier ? (
            <div className="inline-flex items-center gap-1 text-xs font-bold text-slate-950">
              Add {shirtsForNextTier} more {pluralize(shirtsForNextTier, "shirt", "s", true)} to
              <span className="text-green-700">SAVE {formatCurrency(savingsForNextTier)}</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1 text-xs font-bold text-slate-950">
              You’ve reached
              <span className="text-green-700">MAXIMUM SAVINGS!</span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center text-sm font-normal text-gray-700">
          Add quantities to unlock savings
        </div>
      )}
    </div>
  );
}
